import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, LogOut } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail) => {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return String(detail);
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("carmo_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/subscribers`, { headers });
      setData(res.data);
    } catch {
      localStorage.removeItem("carmo_token");
      setToken("");
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("carmo_token", res.data.access_token);
      setToken(res.data.access_token);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("carmo_token");
    setToken("");
    setData(null);
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/subscribers/export`, {
      headers,
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "carmo-inscritos.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-[#FAF9F6] px-6">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={login}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm border border-[#0A0A0A] p-10"
        >
          <p className="font-['Cormorant_Garamond',serif] text-3xl tracking-tight">CARMO</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.4em] text-[#A87A63]">Admin</p>
          <input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mail"
            className="mt-8 w-full border border-[#0A0A0A]/30 bg-transparent px-4 py-3 text-sm outline-none focus:border-[#0A0A0A]"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha"
            className="mt-4 w-full border border-[#0A0A0A]/30 bg-transparent px-4 py-3 text-sm outline-none focus:border-[#0A0A0A]"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-xs text-[#A87A63]">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-6 w-full bg-[#0A0A0A] py-3 text-[11px] uppercase tracking-[0.3em] text-[#FAF9F6] transition-colors duration-300 hover:bg-[#A87A63] disabled:opacity-60"
          >
            {loading ? "Entrando…" : "Entrar"}
          </button>
          <a href="/" className="mt-6 block text-center text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A]/40 hover:text-[#0A0A0A]">
            ← Voltar
          </a>
        </motion.form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF9F6] px-6 py-12 sm:px-12">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-[#0A0A0A] pb-8">
          <div>
            <p className="font-['Cormorant_Garamond',serif] text-4xl tracking-tight">CARMO</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.4em] text-[#A87A63]">
              Lista de espera
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="export-csv-button"
              onClick={exportCsv}
              className="flex items-center gap-2 border border-[#0A0A0A] px-5 py-3 text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-[#0A0A0A] hover:text-[#FAF9F6]"
            >
              <Download size={14} /> Exportar CSV
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.25em] text-[#0A0A0A]/50 transition-colors duration-300 hover:text-[#0A0A0A]"
            >
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>

        <p data-testid="subscriber-count" className="mt-8 text-sm font-light text-[#0A0A0A]/60">
          {data ? `${data.count} inscrita${data.count === 1 ? "" : "s"}` : "Carregando…"}
        </p>

        <div className="mt-4 overflow-x-auto border border-[#0A0A0A]/20">
          <table data-testid="subscribers-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#0A0A0A]/20 text-[10px] uppercase tracking-[0.3em] text-[#0A0A0A]/50">
                <th className="px-5 py-4 font-normal">E-mail</th>
                <th className="px-5 py-4 font-normal">Data</th>
                <th className="px-5 py-4 font-normal">Idioma</th>
                <th className="px-5 py-4 font-normal">Origem</th>
              </tr>
            </thead>
            <tbody>
              {data?.subscribers.map((s) => (
                <tr key={s.email} className="border-b border-[#0A0A0A]/10 last:border-0">
                  <td className="px-5 py-4">{s.email}</td>
                  <td className="px-5 py-4 text-[#0A0A0A]/60">
                    {new Date(s.created_at).toLocaleString("pt-BR")}
                  </td>
                  <td className="px-5 py-4 uppercase text-[#0A0A0A]/60">{s.lang}</td>
                  <td className="px-5 py-4 text-[#0A0A0A]/60">{s.source}</td>
                </tr>
              ))}
              {data && data.subscribers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-[#0A0A0A]/40">
                    Nenhuma inscrita ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
